import { type TutorialLevel } from "./tutorial-data";

const TEST_TUTORIAL: TutorialLevel = {
  name: "Pixellence Basics",
  description: "Learn how to play Pixellence! This tutorial will guide you through the absolute basics.",
  gridLayout: [
    [1, 0, 1, 0, 1],
    [1, 0, 1, 0, 0],
    [1, 1, 1, 0, 1],
    [1, 0, 1, 0, 1],
    [1, 0, 1, 0, 1],
  ],
  phases: [
    {
      waitFor: "message",
      message: (
        <p>
          Pixellence is a game where you{" "}
          <strong>use numerical clues to color squares in a grid</strong>,
          leading to a picture at the end.
        </p>
      ),
    },
    {
      waitFor: "message",
      message: (
        <p>
          The numbers beside a row or column tell you{" "}
          <strong>how many squares</strong> in that row/column{" "}
          <strong>must be colored in</strong>.
        </p>
      ),
      vignettes: [
        {
          type: "allClues",
        },
      ],
    },
    {
      waitFor: "message",
      message: (
        <p>
          For each of these columns, <strong>the "5" clue</strong> tells us that
          the column contains <strong>five colored-in squares</strong>.
        </p>
      ),
      vignettes: [
        {
          type: "columnClue",
          index: 0,
        },
        {
          type: "columnClue",
          index: 2,
        },
      ],
    },
    {
      waitFor: "tileFill",
      message: (
        <p>
          <strong>Color all the squares</strong> in these two columns{" "}
          <strong>by tapping or dragging</strong> to continue.
        </p>
      ),
      allowGridInput: true,
      vignettes: [
        {
          type: "column",
          index: 0,
        },
        {
          type: "column",
          index: 2,
        },
      ],
      requireColor: [
        [0, 0],
        [0, 1],
        [0, 2],
        [0, 3],
        [0, 4],
        [2, 0],
        [2, 1],
        [2, 2],
        [2, 3],
        [2, 4],
      ],
    },
    {
      waitFor: "message",
      message: (
        <p>
          <strong>Great!</strong>
        </p>
      ),
    },
    {
      waitFor: "message",
      message: (
        <p>
          Clues with <strong>multiple numbers</strong> indicate{" "}
          <strong>groups of colored-in squares</strong> separated by one or more
          empty squares.
        </p>
      ),
      vignettes: [
        {
          type: "rowClue",
          index: 2,
        },
      ],
    },
    {
      waitFor: "message",
      message: (
        <p>
          The groups are{" "}
          <strong>always in the order indicated by the clues</strong>. Here,{" "}
          <strong>the group of three squares must be to the left</strong> of the
          single colored-in square.
        </p>
      ),
      vignettes: [
        {
          type: "rowClue",
          index: 2,
        },
      ],
    },
    {
      waitFor: "tileFill",
      allowGridInput: true,
      message: (
        <p>
          <strong>Fill out this row correctly</strong> to continue.
        </p>
      ),
      vignettes: [
        {
          type: "row",
          index: 2,
        },
      ],
      requireColor: [
        [0, 2],
        [1, 2],
        [2, 2],
        [4, 2],
      ],
      requireX: [[3, 2]],
    },
    {
      waitFor: "message",
      message: (
        <p>
          <strong>Excellent!</strong>
        </p>
      ),
    },
    {
      waitFor: "message",
      message: (
        <p>
          A clue of "0" indicates that there are{" "}
          <strong>no colored-in squares</strong> in that row or column.
        </p>
      ),
      vignettes: [
        {
          type: "column",
          index: 3,
        },
      ],
    },
    {
      waitFor: "message",
      message: (
        <p>
          You can <strong>switch</strong> between coloring and X-ing out by{" "}
          <strong>pressing the paintbrush and X buttons</strong>, or by{" "}
          <strong>pressing space</strong> if you're on a computer.
        </p>
      ),
    },
    {
      waitFor: "toolSwitch",
      allowToolSwitch: true,
      highlightXOutButton: true,
      message: (
        <p>
          <strong>Switch to "X-out"</strong> to continue.
        </p>
      ),
    },
    {
      waitFor: "tileFill",
      allowGridInput: true,
      message: (
        <p>
          <strong>X-out each square in this column</strong> to continue.
        </p>
      ),
      vignettes: [
        {
          type: "column",
          index: 3,
        },
      ],
      requireX: [
        [3, 0],
        [3, 1],
        [3, 2],
        [3, 3],
        [3, 4],
      ],
    },
    {
      waitFor: "message",
      message: (
        <>
          <p>
            <strong>Good job!</strong>
          </p>
          <p>
            {" "}
            You should <strong>always X-out squares</strong> that you know{" "}
            <strong>cannot possibly be colored</strong>.
          </p>
        </>
      ),
    },
    {
      waitFor: "message",
      message: (
        <>
          <p>
            You now have all the information needed to{" "}
            <strong>complete this puzzle</strong>.
          </p>
        </>
      ),
    },
    {
      waitFor: "tileFill",
      allowGridInput: true,
      allowToolSwitch: true,
      message: (
        <>
          <p>
            <strong>Fill out this column</strong> and finish the puzzle.
          </p>
        </>
      ),
      vignettes: [
        {
          type: "column",
          index: 4,
        },
      ],
      requireColor: [
        [4, 0],
        [4, 3],
        [4, 4],
      ],
    },
    {
      waitFor: "message",
      message: (
        <>
          <p>
            <strong>Great job!</strong>
          </p>
          <p>You now know the basics of Pixellence!</p>
        </>
      ),
    },
  ],
};

export default TEST_TUTORIAL;
